import type { Map as LeafletMap, Marker } from "leaflet";
import { Loader2, MapPin, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface GPSLocationPickerProps {
  initialLatitude: number;
  initialLongitude: number;
  onLocationSelect: (latitude: number, longitude: number) => void;
}

export const GPSLocationPicker = ({
  initialLatitude,
  initialLongitude,
  onLocationSelect,
}: GPSLocationPickerProps) => {
  const [latitude, setLatitude] = useState(initialLatitude);
  const [longitude, setLongitude] = useState(initialLongitude);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingLocation, setLoadingLocation] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [marker, setMarker] = useState<Marker | null>(null);

  // Get user's location based on IP address
  useEffect(() => {
    const getUserLocationFromIP = async () => {
      // Only fetch if we don't have a specific location set
      if (initialLatitude === 0 && initialLongitude === 0) {
        setLoadingLocation(true);
        try {
          const response = await fetch("https://ipapi.co/json/");
          const data = await response.json();

          if (data.latitude && data.longitude) {
            setLatitude(data.latitude);
            setLongitude(data.longitude);
          } else {
            // Default to New York if IP geolocation fails
            setLatitude(40.7128);
            setLongitude(-74.006);
          }
        } catch (_error) {
          // Default to New York if IP geolocation fails
          setLatitude(40.7128);
          setLongitude(-74.006);
        }
        setLoadingLocation(false);
      }
    };

    getUserLocationFromIP();
  }, [initialLatitude, initialLongitude]);

  useEffect(() => {
    // Initialize map using Leaflet (free alternative to Google Maps)
    const loadMap = async () => {
      // Load Leaflet CSS and JS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(css);
      }

      const windowWithLeaflet = window as Window & { L?: typeof import("leaflet") };
      if (!windowWithLeaflet.L) {
        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        await new Promise((resolve) => {
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }

      const L = (window as Window & { L?: typeof import("leaflet") }).L;
      if (!L) return;

      if (mapRef.current && !map && latitude !== 0 && longitude !== 0) {
        const newMap = L.map(mapRef.current).setView([latitude, longitude], 10);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
        }).addTo(newMap);

        const newMarker = L.marker([latitude, longitude], { draggable: true }).addTo(newMap);

        newMarker.on("dragend", (e: { target: Marker }) => {
          const position = e.target.getLatLng();
          setLatitude(position.lat);
          setLongitude(position.lng);
        });

        newMap.on("click", (e: { latlng: { lat: number; lng: number } }) => {
          const { lat, lng } = e.latlng;
          setLatitude(lat);
          setLongitude(lng);
          newMarker.setLatLng([lat, lng]);
        });

        setMap(newMap);
        setMarker(newMarker);
      }
    };

    if (!loadingLocation) {
      loadMap();
    }

    // Cleanup
    return () => {
      if (map) {
        map.remove();
        setMap(null);
        setMarker(null);
      }
    };
  }, [latitude, longitude, loadingLocation, map]);

  useEffect(() => {
    if (marker && map) {
      marker.setLatLng([latitude, longitude]);
      map.setView([latitude, longitude]);
    }
  }, [latitude, longitude, marker, map]);

  const handleSearch = async () => {
    if (!(searchQuery || "").trim()) return;
    try {
      // Use Nominatim (OpenStreetMap) geocoding service
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newLat = parseFloat(lat);
        const newLon = parseFloat(lon);
        setLatitude(newLat);
        setLongitude(newLon);

        if (map && marker) {
          map.setView([newLat, newLon], 12);
          marker.setLatLng([newLat, newLon]);
        }
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
  };

  const handleConfirm = () => {
    onLocationSelect(latitude, longitude);
  };

  if (loadingLocation) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="text-gray-400">Getting your location...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Search */}
      <div className="flex space-x-2">
        <Input
          placeholder="Search for a location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-gray-700 border-gray-600 text-white"
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button type="button" onClick={handleSearch} variant="outline">
          <Search size={16} />
        </Button>
      </div>

      {/* Coordinates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Latitude</Label>
          <Input
            type="number"
            step="any"
            value={latitude}
            onChange={(e) => setLatitude(parseFloat(e.target.value) || 0)}
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">Longitude</Label>
          <Input
            type="number"
            step="any"
            value={longitude}
            onChange={(e) => setLongitude(parseFloat(e.target.value) || 0)}
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 bg-gray-700 rounded-lg overflow-hidden">
        <div ref={mapRef} className="w-full h-full min-h-[400px]" />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2">
        <Button onClick={handleConfirm} className="bg-green-600 hover:bg-green-700 text-white">
          <MapPin size={16} className="mr-2" />
          Set Location
        </Button>
      </div>

      <p className="text-xs text-gray-400">
        Click on the map or drag the marker to set the GPS location. You can also search for a
        location or enter coordinates manually.
      </p>
    </div>
  );
};
